import React, { type JSX } from 'react';
import {
    Box,
    Container,
    Paper,
    Stack,
    TextField,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    Divider,
    AppBar,
    Toolbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, CustomModal, CustomTransComponent } from 'components';
import { ModalActions } from 'components/CustomModal';
import { ActionCard } from './ActionCard';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
    useCreateLoadBalancingAutomationMutation,
    useGetLoadBalancingAutomationByIdQuery,
    useUpdateLoadBalancingAutomationMutation,
    useGetAllActionByDeviceTypeIdQuery,
} from '@fiji/common/src/features/loadBalancing/loadBalancingApi';
import { setMessageContent, setDisableHeaderBackNavigation } from '@fiji/common/src/features/common/commonSlice';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '@fiji/common';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useGetFilterActionsQuery } from '@fiji/common/src/features/actions/actionsApi';
import {
    useGetLoadGroupByIdQuery,
    useLazyGetLoadGroupByIdQuery,
} from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useLazyGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { Add } from '@mui/icons-material';
import { EmptyState } from '@brightlayer-ui/react-components';
import {
    SaveActionLabel,
    ModalTargetSelector,
    ModalActionRadios,
    TriggerColumnBody,
    EventColumnBody,
    AppBarActions,
} from './AutomationSubComponents';
const DragDropContextAny = DragDropContext as any;
const DroppableAny = Droppable as any;
const DraggableAny = Draggable as any;

const getDeviceIdFromTriggerId = (triggerId: string): string => {
    if (!triggerId || typeof triggerId !== 'string') return '';
    const idx = triggerId.indexOf('_');
    return idx > 0 ? triggerId.slice(0, idx) : '';
};

const collectLoadConfigDeviceTypeIds = (loadConfig: any, ids: Set<string>): void => {
    if (!Array.isArray(loadConfig)) {
        return;
    }
    for (const item of loadConfig) {
        if (item?.deviceTypeId) {
            ids.add(item.deviceTypeId);
        }
    }
};

const collectLoadGroupDeviceTypeIds = (loadGroupData: any): string[] => {
    const ids = new Set<string>();
    collectLoadConfigDeviceTypeIds(loadGroupData?.data?.loadConfig, ids);
    const records = loadGroupData?.data?.records;
    if (Array.isArray(records)) {
        for (const record of records) {
            collectLoadConfigDeviceTypeIds(record?.loadConfig, ids);
        }
    }
    return Array.from(ids);
};

interface LoadGroupActionData {
    id: string;
    actionType: string;
    groupName: string;
    deviceId: string;
    path: string[];
    description: string;
}

interface LoadGroupEvent {
    id: string;
    name: string;
    onSet: boolean;
    onClear: boolean;
    editable?: boolean;
    channelId?: string;
    unit?: string;
    severity?: string;
}

interface CreatedAction {
    id: string;
    actionType: string;
    targetName?: string;
    deviceName?: string;
    deviceLocation?: string;
    path: string[];
    description: string;
    type: 'load' | 'loadGroup' | 'device';
    showPath: boolean;
    userNote?: string;
    deviceIcon?: any;
    targetIds: string[];
    targetNodes: any[];
    enabled?: boolean;
    actionId?: string;
}

const getHydratedActionTypeKey = (isLoad: boolean, isActionLoadGroup: boolean): CreatedAction['type'] => {
    if (isLoad) return 'load';
    if (isActionLoadGroup) return 'loadGroup';
    return 'device';
};

const getHydratedActionIdSegment = (isDevice: boolean, isLoad: boolean): string => {
    if (isDevice) return 'devices';
    if (isLoad) return 'loads';
    return 'groups';
};

const getHydratedActionFallbackLabel = (
    t: any,
    isLoad: boolean,
    isActionLoadGroup: boolean,
    enabled: boolean
): string => {
    if (enabled) {
        if (isLoad) return t('WIDGETS:BALANCING.TURN_ON_LOAD');
        if (isActionLoadGroup) return t('WIDGETS:BALANCING.TURN_ON_LOAD_GROUP');
        return t('WIDGETS:BALANCING.TURN_ON_DEVICE');
    }
    if (isLoad) return t('WIDGETS:BALANCING.TURN_OFF_LOAD');
    if (isActionLoadGroup) return t('WIDGETS:BALANCING.TURN_OFF_LOAD_GROUP');
    return t('WIDGETS:BALANCING.TURN_OFF_DEVICE');
};

const getHydratedMultiTargetLabel = (t: any, isLoad: boolean, isActionLoadGroup: boolean): string => {
    if (isLoad) return t('WIDGETS:BALANCING.MULTIPLE_LOADS');
    if (isActionLoadGroup) return t('WIDGETS:BALANCING.MULTIPLE_LOAD_GROUPS');
    return t('WIDGETS:BALANCING.MULTIPLE_DEVICES');
};

const resolveHydratedActionTypeLabel = (
    t: any,
    isLoad: boolean,
    isActionLoadGroup: boolean,
    actionData: any,
    enabled: boolean
): string => {
    const nameFromAction = actionData?.name;
    if (nameFromAction) return nameFromAction;
    return getHydratedActionFallbackLabel(t, isLoad, isActionLoadGroup, enabled);
};

const buildActionIdFromHydrated = (allActions: any, actionName: any, fallbackId: any): any => {
    const list = allActions?.data;
    const matched = list?.find((act: any) => act.displayName === actionName);
    return matched?.key ?? fallbackId;
};

const buildHydratedTargetNodes = (triggerIds: any[]): any[] =>
    triggerIds.map((triggerObj: any) => ({
        ...triggerObj,
        loadName: triggerObj.loadName ?? triggerObj.name,
    }));

const buildHydratedAction = (a: any, t: any, allActions: any): CreatedAction => {
    const isLoad = a.type === 'LOADS';
    const isActionLoadGroup = a.type === 'LOAD_GROUPS';
    const isDevice = a.type === 'DEVICE';

    const triggerIds = a.triggerIds || [];
    const isSingleTarget = triggerIds.length === 1;

    const targetNodes = buildHydratedTargetNodes(triggerIds);

    const idSegment = getHydratedActionIdSegment(isDevice, isLoad);
    const actionType = resolveHydratedActionTypeLabel(t, isLoad, isActionLoadGroup, a.action, a.enabled);
    const targetName = isSingleTarget ? triggerIds[0].name : getHydratedMultiTargetLabel(t, isLoad, isActionLoadGroup);
    const notes = a.notes || '';

    return {
        id: `${Date.now()}-${idSegment}-${a.order}`,
        actionType,
        type: getHydratedActionTypeKey(isLoad, isActionLoadGroup),
        targetName,
        deviceName: '',
        path: [],
        description: notes,
        showPath: true,
        userNote: notes,
        deviceIcon: undefined,
        targetIds: triggerIds.map((tid: any) => tid.id),
        targetNodes: targetNodes,
        enabled: a.enabled,
        actionId: buildActionIdFromHydrated(allActions, a.action?.name, a.action?.id),
    };
};

const buildHydratedEvents = (data: any, isLoadGroup: boolean): LoadGroupEvent[] => {
    const events: LoadGroupEvent[] = [];
    if (!isLoadGroup && Array.isArray(data?.thresholds) && data.thresholds.length > 0) {
        data.thresholds.forEach((threshold: any) => {
            events.push({
                id: threshold.id,
                name: threshold.name,
                onSet: threshold.onSet,
                onClear: threshold.onClear,
                severity: threshold.severity,
                channelId: threshold.id,
            });
        });
    }
    if (Array.isArray(data?.events) && data.events.length > 0) {
        data.events.forEach((e: any) => {
            events.push({
                id: e.id,
                name: e.name,
                onSet: e.onSet,
                onClear: e.onClear,
            });
        });
    }
    return events;
};

const pickTruthyGroupPathValue = (node: any): any => {
    const raw = node?.groupPath ?? node?.path;
    return raw ?? node?.breadcrumbs;
};

const pickGroupPathFromNames = (node: any): string[] | undefined => {
    const gn = node?.groupName;
    if (typeof gn === 'string') return [gn];
    const parentName = node?.group?.name;
    return typeof parentName === 'string' ? [parentName] : undefined;
};

const pickFirstGroupPathCandidate = (node: any): any =>
    pickTruthyGroupPathValue(node) ?? pickGroupPathFromNames(node) ?? [];

const getNormalizedGroupPathFromNode = (node: any): string[] => {
    const rawPath = pickFirstGroupPathCandidate(node);
    if (!Array.isArray(rawPath)) {
        return [];
    }
    return rawPath.filter(
        (segment: any): segment is string => typeof segment === 'string' && segment.trim().length > 0
    );
};

const hasWebIcon = (candidate: any): boolean => Boolean(candidate?.web);

const normalizeDeviceIconValue = (iconData: any): any => {
    if (hasWebIcon(iconData)) return iconData;
    const statusIcon = iconData?.status?.icon;
    if (hasWebIcon(statusIcon)) return statusIcon;
    const nestedIcon = iconData?.icon;
    if (hasWebIcon(nestedIcon)) return nestedIcon;
    return {};
};

const ACTION_ICON_PICKERS: Record<CreatedAction['type'], (node: any) => any> = {
    loadGroup: (node: any) => normalizeDeviceIconValue(node?.deviceTypeIcon ?? {}),
    load: (node: any) => normalizeDeviceIconValue(node?.deviceIcon ?? node?.icon ?? {}),
    device: (node: any) => normalizeDeviceIconValue(node?.icon ?? {}),
};

const getActionTargetIconValue = (node: any, type: CreatedAction['type']): any => {
    if (!node) return {};
    return ACTION_ICON_PICKERS[type](node);
};

const findDeviceIconInTreeNode = (node: any, targetDeviceId: string): any => {
    if (!node) return null;
    if (node.id === targetDeviceId && node.type === 'DEVICE' && node.icon) {
        return node.icon;
    }
    if (Array.isArray(node.children)) {
        for (const child of node.children) {
            const found = findDeviceIconInTreeNode(child, targetDeviceId);
            if (found) return found;
        }
    }
    return null;
};

const trimIfNonEmptyString = (value: any): string => {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    return trimmed;
};

const extractArrayErrorMessage = (payload: any): string => {
    if (!Array.isArray(payload) || payload.length === 0) return '';
    return String(payload[0] || '').trim();
};

const extractApiErrorMessage = (error: any): string => {
    const messagePayload = error?.data?.errorMessage;
    const fromArray = extractArrayErrorMessage(messagePayload);
    if (fromArray) return fromArray;
    if (Array.isArray(messagePayload)) return '';
    const fromPayload = trimIfNonEmptyString(messagePayload);
    if (fromPayload) return fromPayload;
    return trimIfNonEmptyString(error?.error);
};

type LoadGroupFetchResult = {
    isMultipleDevice: boolean;
    deviceNames: Set<string>;
    groupPaths: string[][];
    deviceTypeIcon: any;
};

type LoadGroupEnrichment = {
    deviceName: string;
    path: string[];
    deviceIcon: any;
};

type DeviceActionEnrichment = {
    path: string[];
    deviceIcon: any;
};

const arePathsIdentical = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((seg, i) => seg === b[i]);

const findDynamicActionByKey = (actionsData: any, key: string): any =>
    actionsData?.data?.find((a: any) => a.key === key);

const resolveActionTypeLabel = (dynamicAction: any, fallback: string): string => dynamicAction?.displayName || fallback;

const resolveActionEnabled = (dynamicAction: any): boolean => dynamicAction?.default === 1;

const readFirstElement = (arr: any): any => arr?.[0];

const getNodeDeviceIdPrimary = (node: any): string | undefined => node?.deviceId ?? node?.device?.id;
const getNodeDeviceIdParent = (node: any): string | undefined => node?.parentDeviceId ?? node?.parent?.deviceId;

const getNodeDeviceNamePrimary = (node: any): string | undefined => node?.deviceName ?? node?.device?.name;
const getNodeDeviceNameParent = (node: any): string | undefined => node?.parentDeviceName ?? node?.parent?.deviceName;

const resolveEditActionModalType = (type: CreatedAction['type']): string => {
    if (type === 'load') return 'loads';
    if (type === 'loadGroup') return 'loadGroups';
    return 'devices';
};

const resolveEditActionIdByEnabled = (dynamicActionsData: any, enabled: boolean): string => {
    const match = dynamicActionsData?.data?.find((a: any) => a.default === (enabled ? 1 : 0));
    return match?.key || '';
};

const resolveEditActionId = (actionToEdit: CreatedAction, dynamicActionsData: any): string => {
    const stored = actionToEdit.actionId || '';
    if (actionToEdit.type === 'device' || stored) return stored;
    return resolveEditActionIdByEnabled(dynamicActionsData, !!actionToEdit.enabled);
};

const isResolveEditActionEffectSkipped = (
    editingActionId: string | null,
    isActionModalOpen: boolean,
    modalTriggerType: string,
    dynamicActionsData: any,
    isDynamicActionsFetching: boolean
): boolean => {
    if (!editingActionId || !isActionModalOpen) return true;
    if (modalTriggerType === 'devices' || modalTriggerType === '') return true;
    return !dynamicActionsData?.data || isDynamicActionsFetching;
};

const findResolvedActionKey = (dynamicActionsData: any, actionToEdit: CreatedAction): string | undefined => {
    const list = dynamicActionsData.data;
    if (actionToEdit.actionId && list.some((a: any) => a.key === actionToEdit.actionId)) {
        return actionToEdit.actionId;
    }
    const byEnabled = list.find((a: any) => a.default === (actionToEdit.enabled ? 1 : 0));
    if (byEnabled) return byEnabled.key;
    const byName = list.find((a: any) => a.displayName === actionToEdit.actionType);
    return byName?.key;
};

const collectLoadGroupResultFields = (data: any): LoadGroupFetchResult => {
    const deviceNames = new Set<string>();
    const groupPaths: string[][] = [];
    const loadConfig = data.loadConfig || [];
    for (const lc of loadConfig) {
        if (lc.deviceName) deviceNames.add(lc.deviceName);
        if (Array.isArray(lc.groupPath) && lc.groupPath.length > 0) {
            groupPaths.push(lc.groupPath);
        }
    }
    return {
        isMultipleDevice: data.loadGroup?.isMultipleDevice ?? false,
        deviceNames,
        groupPaths,
        deviceTypeIcon: data.deviceTypeIcon ?? {},
    };
};

const fetchLoadGroupResult = async (
    getLoadGroupById: any,
    loadGroupId: string
): Promise<LoadGroupFetchResult | null> => {
    try {
        const result = await getLoadGroupById({ loadGroupId }).unwrap();
        const data = result?.data;
        if (!data) return null;
        return collectLoadGroupResultFields(data);
    } catch (err) {
        console.error('Failed to fetch load group details for enrichment:', loadGroupId, err);
        return null;
    }
};

const buildSingleLoadGroupEnrichment = (t: any, lgData: LoadGroupFetchResult): LoadGroupEnrichment => {
    if (lgData.isMultipleDevice) {
        return {
            deviceName: t('WIDGETS:BALANCING.MULTIPLE_DEVICES'),
            path: [],
            deviceIcon: normalizeDeviceIconValue(lgData.deviceTypeIcon),
        };
    }
    const deviceName = lgData.deviceNames.size === 1 ? Array.from(lgData.deviceNames)[0] : '';
    const path = lgData.groupPaths.length > 0 ? [deviceName, ...lgData.groupPaths[0]].filter(Boolean) : [];
    return {
        deviceName,
        path,
        deviceIcon: normalizeDeviceIconValue(lgData.deviceTypeIcon),
    };
};

const buildMultiDeviceLoadGroupEnrichment = (t: any): LoadGroupEnrichment => ({
    deviceName: t('WIDGETS:BALANCING.MULTIPLE_DEVICES'),
    path: [],
    deviceIcon: undefined,
});

const buildSameDeviceMultiLoadGroupEnrichment = (
    results: LoadGroupFetchResult[],
    deviceName: string
): LoadGroupEnrichment => {
    const allPaths = results.flatMap((r) => r.groupPaths);
    const firstPath = allPaths[0];
    const allSamePath = Boolean(firstPath) && allPaths.every((p) => arePathsIdentical(p, firstPath));
    const path = allSamePath && firstPath ? [deviceName, ...firstPath].filter(Boolean) : [];
    return {
        deviceName,
        path,
        deviceIcon: normalizeDeviceIconValue(results[0].deviceTypeIcon),
    };
};

const buildMultiLoadGroupEnrichment = (t: any, results: LoadGroupFetchResult[]): LoadGroupEnrichment => {
    if (results.some((r) => r.isMultipleDevice)) return buildMultiDeviceLoadGroupEnrichment(t);
    const allDeviceNames = new Set<string>();
    results.forEach((r) => r.deviceNames.forEach((n) => allDeviceNames.add(n)));
    if (allDeviceNames.size !== 1) return buildMultiDeviceLoadGroupEnrichment(t);
    return buildSameDeviceMultiLoadGroupEnrichment(results, Array.from(allDeviceNames)[0]);
};

const collectTargetGroupPaths = (targetIds: string[], deviceDataMap: Map<string, any>): string[][] => {
    const paths: string[][] = [];
    targetIds.forEach((devId) => {
        const devData = deviceDataMap.get(devId);
        if (devData?.groupPath && Array.isArray(devData.groupPath)) {
            paths.push(devData.groupPath);
        }
    });
    return paths;
};

const pickFirstMatchedDeviceIcon = (targetIds: string[], deviceIconMap: Map<string, any>): any => {
    const firstDevId = targetIds.find((id) => deviceIconMap.has(id));
    return firstDevId ? normalizeDeviceIconValue(deviceIconMap.get(firstDevId) ?? {}) : undefined;
};

const buildDeviceActionEnrichmentFromMaps = (
    devAction: CreatedAction,
    deviceDataMap: Map<string, any>,
    deviceIconMap: Map<string, any>,
    t: any
): DeviceActionEnrichment => {
    const targetGroupPaths = collectTargetGroupPaths(devAction.targetIds, deviceDataMap);
    const deviceIcon = pickFirstMatchedDeviceIcon(devAction.targetIds, deviceIconMap);
    if (devAction.targetIds.length === 1) {
        return { path: targetGroupPaths[0] || [], deviceIcon };
    }
    const firstPath = targetGroupPaths[0];
    const allSame = Boolean(firstPath) && targetGroupPaths.every((p) => arePathsIdentical(p, firstPath));
    if (allSame && firstPath) return { path: firstPath, deviceIcon };
    return { path: [t('WIDGETS:BALANCING.MULTIPLE_GROUPS')], deviceIcon };
};

const sortAndBuildHydratedActions = (data: any, t: any, allActions: any): CreatedAction[] =>
    (data.balancingActions || [])
        .slice()
        .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
        .map((a: any) => buildHydratedAction(a, t, allActions));

const hasAnyEventSelected = (events: LoadGroupEvent[]): boolean =>
    events.some((e) => e.onSet === true || e.onClear === true);

const isModalTargetTypeValid = (modalTriggerType: string): boolean =>
    modalTriggerType === 'loads' || modalTriggerType === 'loadGroups' || modalTriggerType === 'devices';

const shouldSkipDynamicActionsQuery = (deviceTypeIds: string[], modalTriggerType: string): boolean =>
    deviceTypeIds.length === 0 || modalTriggerType === 'devices';

const buildDynamicActionsBody = (deviceTypeIds: string[], modalTriggerType: string): any => ({
    deviceTypeIds,
    ...(modalTriggerType === 'loadGroups' ? { includeOnlyLoadGroupActions: true } : { includeOnlyLoadActions: true }),
});

const readLocationInfo = (locationState: any): { defaultTrigger: string; previousRoute: any } => {
    const state = locationState || {};
    return { defaultTrigger: state.defaultTrigger || '', previousRoute: state.from };
};

const resolveInitialTrigger = (isCreateMode: boolean, defaultTrigger: string): string =>
    isCreateMode ? defaultTrigger : '';

type DeviceIconLookup = { hit: boolean; icon: any };

type GroupDeviceIdsResult = { devices: string[]; groups: string[] };

type ActionsListSectionProps = {
    tempLoadGroupActions: LoadGroupActionData[];
    createdActions: CreatedAction[];
    theme: any;
    t: any;
    onDragEnd: (result: DropResult) => void;
    onDeleteLoadGroupAction: (id: string) => void;
    onEditAction: (id: string) => void;
    onDeleteAction: (id: string) => void;
};

function handleDragEndReorder(result: DropResult, items: any[]): any[] | null {
    const { source, destination } = result;
    if (!destination) {
        return null;
    }
    const reordered = Array.from(items);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    return reordered;
}

const isTextFieldError = (length: number, limit: number, errorMessage: string): boolean =>
    length > limit || Boolean(errorMessage);

const CharCounterHelperText = ({
    errorMessage,
    length,
    limit,
}: {
    errorMessage: string;
    length: number;
    limit: number;
}): JSX.Element => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color={errorMessage ? 'error' : 'text.secondary'}>
            {errorMessage || ' '}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {`${length}/${limit}`}
        </Typography>
    </Box>
);

const ActionsListEmptyState = ({ theme, t }: { theme: any; t: any }): JSX.Element => (
    <EmptyState
        icon={<DirectionsRunIcon sx={{ fontSize: 100, color: theme.palette.action.active }} />}
        title={t('WIDGETS:BALANCING.ACTION_HELPER')}
    />
);

type TempActionCardProps = {
    actionData: any;
    canDeleteAction: boolean;
    onDelete: (id: string) => void;
};

const TempActionCard = ({ actionData, canDeleteAction, onDelete }: TempActionCardProps): JSX.Element => (
    <ActionCard
        actionType={actionData.actionType}
        targetName={actionData.groupName}
        deviceName={actionData.deviceName}
        path={actionData.path}
        description={actionData.description}
        onDelete={canDeleteAction ? (): void => onDelete(actionData.id) : undefined}
    />
);

type CreatedDraggableCardProps = {
    actionItem: CreatedAction;
    index: number;
    canDeleteAction: boolean;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
};

const renderDraggableActionCard = (
    actionItem: CreatedAction,
    canDeleteAction: boolean,
    onEdit: (id: string) => void,
    onDelete: (id: string) => void,
    draggableProvided: any
): JSX.Element => (
    <ActionCard
        ref={draggableProvided.innerRef}
        draggableProps={draggableProvided.draggableProps}
        dragHandleProps={draggableProvided.dragHandleProps}
        actionType={actionItem.actionType}
        targetName={actionItem.targetName || ''}
        deviceName={actionItem.deviceName || ''}
        path={actionItem.path}
        description={actionItem.userNote || ''}
        showPath={actionItem.showPath}
        onEdit={(): void => onEdit(actionItem.id)}
        onDelete={canDeleteAction ? (): void => onDelete(actionItem.id) : undefined}
        deviceIcon={actionItem.deviceIcon}
    />
);

const CreatedDraggableCard = ({
    actionItem,
    index,
    canDeleteAction,
    onEdit,
    onDelete,
}: CreatedDraggableCardProps): JSX.Element => (
    <DraggableAny draggableId={actionItem.id} index={index}>
        {(draggableProvided: any): JSX.Element =>
            renderDraggableActionCard(actionItem, canDeleteAction, onEdit, onDelete, draggableProvided)
        }
    </DraggableAny>
);

type ActionsDroppableListProps = Omit<ActionsListSectionProps, 'theme' | 't' | 'onDragEnd'> & {
    canDeleteAction: boolean;
};

const renderActionsDroppableContent = (props: ActionsDroppableListProps, provided: any): JSX.Element => (
    <div {...provided.droppableProps} ref={provided.innerRef}>
        {props.tempLoadGroupActions.map((actionData: any) => (
            <TempActionCard
                key={actionData.id}
                actionData={actionData}
                canDeleteAction={props.canDeleteAction}
                onDelete={props.onDeleteLoadGroupAction}
            />
        ))}
        {props.createdActions.map((actionItem: CreatedAction, index: number) => (
            <CreatedDraggableCard
                key={actionItem.id}
                actionItem={actionItem}
                index={index}
                canDeleteAction={props.canDeleteAction}
                onEdit={props.onEditAction}
                onDelete={props.onDeleteAction}
            />
        ))}
        {provided.placeholder}
    </div>
);

const ActionsDroppableList = (props: ActionsDroppableListProps): JSX.Element => (
    <DroppableAny droppableId="actions-list">
        {(provided: any): JSX.Element => renderActionsDroppableContent(props, provided)}
    </DroppableAny>
);

const ActionsListSection = ({
    tempLoadGroupActions,
    createdActions,
    theme,
    t,
    onDragEnd,
    onDeleteLoadGroupAction,
    onEditAction,
    onDeleteAction,
}: ActionsListSectionProps): JSX.Element => {
    const totalActions = tempLoadGroupActions.length + createdActions.length;
    const canDeleteAction = totalActions > 1;
    const hasActions = totalActions > 0;

    if (!hasActions) {
        return <ActionsListEmptyState theme={theme} t={t} />;
    }

    return (
        <DragDropContextAny onDragEnd={onDragEnd}>
            <ActionsDroppableList
                tempLoadGroupActions={tempLoadGroupActions}
                createdActions={createdActions}
                canDeleteAction={canDeleteAction}
                onDeleteLoadGroupAction={onDeleteLoadGroupAction}
                onEditAction={onEditAction}
                onDeleteAction={onDeleteAction}
            />
        </DragDropContextAny>
    );
};

export const LoadBalancingAutomation: React.FC = (): JSX.Element => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const [getDeviceById] = useLazyGetDeviceByIdQuery();
    const [getLoadGroupById] = useLazyGetLoadGroupByIdQuery();
    const [getAllGroups] = useGetAllGroupsMutation();
    const hasEnrichedActionsRef = React.useRef<string | null>(null);
    const hasEnrichedLoadGroupActionsRef = React.useRef<string | null>(null);
    const hasEnrichedDeviceActionsRef = React.useRef<string | null>(null);
    const deviceIconCacheRef = React.useRef<Map<string, any>>(new Map());
    const initialHydratedEventsRef = React.useRef<LoadGroupEvent[]>([]);
    const hasHydratedRef = React.useRef<boolean>(false);

    const { data: allActions }: any = useGetFilterActionsQuery(
        { body: {}, params: { all: true } },
        {
            refetchOnMountOrArgChange: true,
            skip: !currentOrg?.id,
        }
    );

    const [createAutomation, { isLoading: isCreatingAutomation }] = useCreateLoadBalancingAutomationMutation();
    const [updateAutomation, { isLoading: isUpdatingAutomation }] = useUpdateLoadBalancingAutomationMutation();

    const isSaving = isCreatingAutomation || isUpdatingAutomation;
    // Disable the header cross-button (Close icon) while a save is in progress.
    React.useEffect(() => {
        dispatch(setDisableHeaderBackNavigation(isSaving));
        return () => {
            dispatch(setDisableHeaderBackNavigation(false));
        };
    }, [isSaving, dispatch]);

    const [action, setAction] = React.useState('');
    const NAME_CHAR_LIMIT = 32;
    const DESC_CHAR_LIMIT = 100;
    const [automationName, setAutomationName] = React.useState('');
    const [automationDescription, setAutomationDescription] = React.useState('');
    const [automationNameError, setAutomationNameError] = React.useState('');
    const [automationDescriptionError, setAutomationDescriptionError] = React.useState('');
    const [hasAttemptedSave, setHasAttemptedSave] = React.useState(false);
    const snapshotRef = React.useRef<string | null>(null);
    const hasUserEditedNameRef = React.useRef(false);
    const hasUserEditedDescriptionRef = React.useRef(false);

    const { balancingId } = useParams();
    const isEditMode = !!balancingId;
    const isCreateMode = !isEditMode;

    const { data: automationDetails } = useGetLoadBalancingAutomationByIdQuery(balancingId!, {
        skip: isCreateMode,
        refetchOnMountOrArgChange: true,
    });

    const location = useLocation();
    const { defaultTrigger: initialDefaultTrigger, previousRoute } = readLocationInfo(location.state);

    // Intercept the header X-icon back navigation so it also lands on the balancing tab.
    React.useEffect(() => {
        const handleHeaderBack = (e: Event): void => {
            e.preventDefault();
            if (previousRoute) {
                navigate(previousRoute, { state: { subTab: 'balancing' } });
            } else {
                navigate(-1);
            }
        };
        window.addEventListener('app:header-back', handleHeaderBack);
        return () => {
            window.removeEventListener('app:header-back', handleHeaderBack);
        };
    }, [previousRoute, navigate]);

    // Edit from navigation state - Only use defaults from state in CREATE mode
    const initialTrigger = resolveInitialTrigger(isCreateMode, initialDefaultTrigger);
    const initialActions: CreatedAction[] = [];

    const [triggerType, setTriggerType] = React.useState<string>(initialTrigger);
    const [modalTriggerType, setModalTriggerType] = React.useState<string>('');
    const [modalSelectedNodes, setModalSelectedNodes] = React.useState<any[]>([]);
    const [actionNote, setActionNote] = React.useState('');

    const handleTriggerTypeChange = (event: any): void => {
        setTriggerType(event.target.value);
        setHasOnSetChecked(false);
        setSelectedLoadId('');
    };

    const handleModalTriggerTypeChange = (event: any): void => {
        setModalTriggerType(event.target.value);
        setAction('');
        setModalSelectedLoadId(event.target.value === 'loads' ? [] : '');
        setModalSelectedNodes([]);
    };

    const handleTriggerLoadSelect = React.useCallback((loadNode: any) => {
        setSelectedLoadId(loadNode.id);
        setSelectedDeviceId(loadNode.deviceId);
        setSelectedDeviceTypeId(loadNode.deviceTypeId);
        setSelectedGroupId(loadNode.groupId);
        setTempLoadGroupActions([]);
    }, []);

    const handleModalLoadSelect = React.useCallback((loadNode: any) => {
        setModalSelectedLoadId((prev: any) => {
            const current = Array.isArray(prev) ? prev : [];
            if (current.includes(loadNode.id)) {
                return current.filter((id) => id !== loadNode.id);
            }
            return [...current, loadNode.id];
        });
        setModalSelectedNodes((prev) => {
            if (prev.find((n) => n.id === loadNode.id)) {
                return prev.filter((n) => n.id !== loadNode.id);
            }
            return [...prev, loadNode];
        });
        setTempLoadGroupActions([]);
    }, []);

    const isNameRequired = true;

    const getAutomationNameError = (value: string, validateRequired: boolean): string => {
        if (value.trim().length > NAME_CHAR_LIMIT) {
            return t('WIDGETS:BALANCING.AUTOMATION_NAME_LIMIT', { limit: NAME_CHAR_LIMIT });
        }

        if (validateRequired && isNameRequired && !value.trim()) {
            return t('WIDGETS:BALANCING.AUTOMATION_NAME_REQUIRED');
        }

        return '';
    };

    const validateAutomationName = (value: string, validateRequired: boolean = hasAttemptedSave): boolean => {
        const errorMessage = getAutomationNameError(value, validateRequired);
        setAutomationNameError(errorMessage);
        return !errorMessage;
    };

    const validateAutomationDescription = (value: string): void => {
        if (value.trim().length > DESC_CHAR_LIMIT) {
            setAutomationDescriptionError(t('WIDGETS:BALANCING.AUTOMATION_DESC_LIMIT', { limit: DESC_CHAR_LIMIT }));
            return;
        }

        setAutomationDescriptionError('');
    };

    const getApiErrorMessage = (error: any): string => extractApiErrorMessage(error);

    const handleAutomationNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value.slice(0, NAME_CHAR_LIMIT);
        hasUserEditedNameRef.current = true;
        setAutomationName(value);
        validateAutomationName(value);
    };

    const [selectedLoadId, setSelectedLoadId] = React.useState<string>('');
    const [selectedDeviceId, setSelectedDeviceId] = React.useState<string>('');
    const [selectedDeviceTypeId, setSelectedDeviceTypeId] = React.useState<string>('');
    const [selectedGroupId, setSelectedGroupId] = React.useState<string>('');

    const [modalSelectedLoadId, setModalSelectedLoadId] = React.useState<any>([]);

    const [hasOnSetChecked, setHasOnSetChecked] = React.useState<boolean>(false);

    const [tempLoadGroupActions, setTempLoadGroupActions] = React.useState<LoadGroupActionData[]>([]);

    const [createdActions, setCreatedActions] = React.useState<CreatedAction[]>(initialActions);
    const [triggerEvents, setTriggerEvents] = React.useState<LoadGroupEvent[]>([]);

    const { data: loadGroupData } = useGetLoadGroupByIdQuery(
        { loadGroupId: selectedLoadId },
        { skip: triggerType !== 'loadGroups' || !selectedLoadId }
    );

    // Compute deviceTypeIds for the events API
    const deviceTypeIds = React.useMemo((): string[] => {
        if (triggerType === 'loads') {
            return selectedDeviceTypeId ? [selectedDeviceTypeId] : [];
        }
        if (triggerType === 'loadGroups') {
            return collectLoadGroupDeviceTypeIds(loadGroupData);
        }
        return [];
    }, [triggerType, selectedDeviceTypeId, loadGroupData]);

    const skipDynamicActions = shouldSkipDynamicActionsQuery(deviceTypeIds, modalTriggerType);
    const dynamicActionsBody = buildDynamicActionsBody(deviceTypeIds, modalTriggerType);

    const {
        data: dynamicActionsData,
        isLoading: isDynamicActionsLoading,
        isFetching: isDynamicActionsFetching,
    } = useGetAllActionByDeviceTypeIdQuery(
        {
            params: { all: false },
            payload: dynamicActionsBody,
        },
        {
            skip: skipDynamicActions,
            refetchOnMountOrArgChange: true,
        }
    );

    const getFormFingerprint = React.useCallback(
        (): string =>
            JSON.stringify({
                name: automationName,
                description: automationDescription,
                triggerType,
                selectedLoadId,
                triggerEvents: triggerEvents.map((e) => ({ onSet: e.onSet, onClear: e.onClear })),
                actions: createdActions.map((a) => ({
                    type: a.type,
                    targetIds: a.targetIds,
                    enabled: a.enabled,
                    actionId: a.actionId,
                    userNote: a.userNote,
                })),
            }),
        [automationName, automationDescription, triggerType, selectedLoadId, triggerEvents, createdActions]
    );

    const isDirty = React.useMemo(() => {
        if (!isEditMode) return true;
        if (!snapshotRef.current) return false;
        return getFormFingerprint() !== snapshotRef.current;
    }, [isEditMode, getFormFingerprint]);

    const applyTriggerMetadata = (data: any): void => {
        const trigger = data.balancingTriggers;
        if (!trigger) {
            setSelectedLoadId('');
            return;
        }
        setSelectedLoadId(trigger.id || '');
        const groupDevices = data.groupDevices;
        const firstDeviceType = readFirstElement(data.deviceType);
        const firstDevice = readFirstElement(groupDevices?.devices);
        const firstGroup = readFirstElement(groupDevices?.groups);
        if (firstDeviceType) setSelectedDeviceTypeId(firstDeviceType.id);
        if (firstDevice) setSelectedDeviceId(firstDevice.id);
        if (firstGroup) setSelectedGroupId(firstGroup.id);
    };

    // Hydrate state from API response in edit mode
    React.useEffect(() => {
        if (!automationDetails?.data) return;
        if (hasHydratedRef.current) return;

        const data = automationDetails.data;
        if (!hasUserEditedNameRef.current) {
            setAutomationName(data.name || '');
        }
        if (!hasUserEditedDescriptionRef.current) {
            setAutomationDescription(data.description || '');
        }
        const isLoadGroup = data.triggerCategory === 'LOAD_GROUPS';
        setTriggerType(isLoadGroup ? 'loadGroups' : 'loads');

        applyTriggerMetadata(data);

        const hydratedEvents = buildHydratedEvents(data, isLoadGroup);

        initialHydratedEventsRef.current = hydratedEvents;
        setTriggerEvents(hydratedEvents);
        setHasOnSetChecked(hasAnyEventSelected(hydratedEvents));

        setCreatedActions(sortAndBuildHydratedActions(data, t, allActions));
        hasHydratedRef.current = true;
    }, [automationDetails, allActions]);

    // Enrich LOADS action targetNodes with device metadata from getDeviceById
    React.useEffect(() => {
        if (!isEditMode || createdActions.length === 0) return;
        // Guard: only enrich once per automation load
        const enrichKey = `${balancingId}_${createdActions.map((a) => a.id).join(',')}`;
        if (hasEnrichedActionsRef.current === enrichKey) return;

        const loadActions = createdActions.filter(
            (a) => a.type === 'load' && a.targetIds.length > 0 && !a.targetNodes?.[0]?.deviceId
        );
        if (loadActions.length === 0) {
            hasEnrichedActionsRef.current = enrichKey;
            return;
        }

        const enrichActions = async (): Promise<void> => {
            // Collect unique deviceIds across all LOADS actions
            const deviceIdToActionIndices = new Map<string, number[]>();
            const actionIndexToDeviceId = new Map<number, string>();
            const multiDeviceActionIndices = new Set<number>();
            loadActions.forEach((loadAction) => {
                const uniqueDeviceIds = new Set<string>();
                loadAction.targetIds.forEach((tid) => {
                    const parsedId = getDeviceIdFromTriggerId(tid);
                    if (parsedId) uniqueDeviceIds.add(parsedId);
                });
                const idx = createdActions.indexOf(loadAction);
                if (uniqueDeviceIds.size === 1) {
                    const devId = Array.from(uniqueDeviceIds)[0];
                    if (!deviceIdToActionIndices.has(devId)) {
                        deviceIdToActionIndices.set(devId, []);
                    }
                    deviceIdToActionIndices.get(devId)!.push(idx);
                    actionIndexToDeviceId.set(idx, devId);
                } else if (uniqueDeviceIds.size > 1) {
                    multiDeviceActionIndices.add(idx);
                }
            });

            if (deviceIdToActionIndices.size === 0 && multiDeviceActionIndices.size === 0) {
                hasEnrichedActionsRef.current = enrichKey;
                return;
            }

            // Fetch device details for each unique deviceId
            const deviceDataMap = new Map<string, any>();
            const deviceIconMap = new Map<string, any>();

            // First, fetch device details from getDeviceById
            await Promise.all(
                Array.from(deviceIdToActionIndices.keys()).map(async (devId) => {
                    try {
                        const result = await getDeviceById(devId).unwrap();
                        if (result?.data) {
                            deviceDataMap.set(devId, result.data);
                        }
                    } catch (err) {
                        console.error('Failed to fetch device details for enrichment:', devId, err);
                    }
                })
            );

            // Then, fetch device icons from hierarchy for all devices in deviceDataMap
            await Promise.all(
                Array.from(deviceDataMap.keys()).map(async (devId) => {
                    const deviceData = deviceDataMap.get(devId);
                    const icon = await fetchDeviceIconFromHierarchy(devId, deviceData?.groupId);
                    deviceIconMap.set(devId, icon);
                })
            );

            if (deviceDataMap.size === 0 && multiDeviceActionIndices.size === 0) {
                hasEnrichedActionsRef.current = enrichKey;
                return;
            }

            // Enrich createdActions with fetched device data
            const enriched = createdActions.map((createdActionItem, idx) => {
                // Multi-device action
                if (multiDeviceActionIndices.has(idx)) {
                    return {
                        ...createdActionItem,
                        deviceName: t('WIDGETS:BALANCING.MULTIPLE_DEVICES'),
                        path: [],
                        deviceIcon: undefined,
                    };
                }
                const mappedDeviceId = actionIndexToDeviceId.get(idx);
                const matchedDeviceData = mappedDeviceId ? deviceDataMap.get(mappedDeviceId) : null;
                if (!matchedDeviceData) return createdActionItem;

                const deviceId = mappedDeviceId || matchedDeviceData.id;
                const deviceName = matchedDeviceData.name || '';
                const groupId = matchedDeviceData.groupId || '';
                const groupPath = matchedDeviceData.groupPath || [];

                // Use icon from hierarchy (fetched) instead of from getDeviceById response
                const hierarchyIcon = deviceIconMap.get(deviceId);
                const deviceIcon = normalizeDeviceIconValue(hierarchyIcon ?? {});

                const enrichedNodes = createdActionItem.targetNodes.map((node: any) => ({
                    ...node,
                    deviceId,
                    deviceName,
                    groupId,
                    groupPath,
                    path: groupPath,
                    breadcrumbs: groupPath,
                }));

                return {
                    ...createdActionItem,
                    deviceName,
                    path: groupPath,
                    deviceIcon,
                    targetNodes: enrichedNodes,
                };
            });

            hasEnrichedActionsRef.current = enrichKey;
            setCreatedActions(enriched);

            // Re-take snapshot after enrichment so isDirty is not falsely true
            requestAnimationFrame(() => {
                snapshotRef.current = JSON.stringify({
                    name: automationName,
                    description: automationDescription,
                    triggerType,
                    selectedLoadId,
                    triggerEvents: triggerEvents.map((e) => ({ onSet: e.onSet, onClear: e.onClear })),
                    actions: enriched.map((a) => ({
                        type: a.type,
                        targetIds: a.targetIds,
                        enabled: a.enabled,
                        actionId: a.actionId,
                        userNote: a.userNote,
                    })),
                });
            });
        };

        void enrichActions();
    }, [createdActions, isEditMode, balancingId]);

    // Enrich LOAD_GROUPS action targets with data from getLoadGroupById
    React.useEffect(() => {
        if (!isEditMode || createdActions.length === 0) return;
        const enrichKey = `${balancingId}_lg_${createdActions.map((a) => a.id).join(',')}`;
        if (hasEnrichedLoadGroupActionsRef.current === enrichKey) return;

        const loadGroupActions = createdActions.filter(
            (a) => a.type === 'loadGroup' && a.targetIds.length > 0 && !a.deviceName
        );
        if (loadGroupActions.length === 0) {
            hasEnrichedLoadGroupActionsRef.current = enrichKey;
            return;
        }

        const enrichLoadGroupActions = async (): Promise<void> => {
            // For each loadGroup action, fetch getLoadGroupById for every target
            const actionEnrichmentMap = new Map<number, LoadGroupEnrichment>();

            await Promise.all(
                loadGroupActions.map(async (lgAction) => {
                    const actionIdx = createdActions.indexOf(lgAction);
                    const isSingleTarget = lgAction.targetIds.length === 1;

                    const fetched = await Promise.all(
                        lgAction.targetIds.map((loadGroupId) => fetchLoadGroupResult(getLoadGroupById, loadGroupId))
                    );
                    const loadGroupResults = fetched.filter((r): r is LoadGroupFetchResult => r !== null);

                    if (loadGroupResults.length === 0) return;

                    const enrichment = isSingleTarget
                        ? buildSingleLoadGroupEnrichment(t, loadGroupResults[0])
                        : buildMultiLoadGroupEnrichment(t, loadGroupResults);
                    actionEnrichmentMap.set(actionIdx, enrichment);
                })
            );

            if (actionEnrichmentMap.size === 0) {
                hasEnrichedLoadGroupActionsRef.current = enrichKey;
                return;
            }

            const enriched = createdActions.map((actionItem, idx) => {
                const enrichment = actionEnrichmentMap.get(idx);
                if (!enrichment) return actionItem;
                return {
                    ...actionItem,
                    deviceName: enrichment.deviceName,
                    path: enrichment.path,
                    deviceIcon: enrichment.deviceIcon,
                };
            });

            hasEnrichedLoadGroupActionsRef.current = enrichKey;
            setCreatedActions(enriched);

            // Re-take snapshot after enrichment so isDirty is not falsely true
            requestAnimationFrame(() => {
                snapshotRef.current = JSON.stringify({
                    name: automationName,
                    description: automationDescription,
                    triggerType,
                    selectedLoadId,
                    triggerEvents: triggerEvents.map((e) => ({ onSet: e.onSet, onClear: e.onClear })),
                    actions: enriched.map((a) => ({
                        type: a.type,
                        targetIds: a.targetIds,
                        enabled: a.enabled,
                        actionId: a.actionId,
                        userNote: a.userNote,
                    })),
                });
            });
        };

        void enrichLoadGroupActions();
    }, [createdActions, isEditMode, balancingId]);

    // Enrich DEVICE action targets with data from getDeviceById
    React.useEffect(() => {
        if (!isEditMode || createdActions.length === 0) return;
        const enrichKey = `${balancingId}_dev_${createdActions.map((a) => a.id).join(',')}`;
        if (hasEnrichedDeviceActionsRef.current === enrichKey) return;

        const deviceActions = createdActions.filter(
            (a) => a.type === 'device' && a.targetIds.length > 0 && !a.path?.length
        );
        if (deviceActions.length === 0) {
            hasEnrichedDeviceActionsRef.current = enrichKey;
            return;
        }

        const enrichDeviceActions = async (): Promise<void> => {
            const actionEnrichmentMap = new Map<number, DeviceActionEnrichment>();

            // Collect all unique device IDs across all DEVICE actions
            const allDeviceIds = new Set<string>();
            deviceActions.forEach((da) => da.targetIds.forEach((id) => allDeviceIds.add(id)));

            // Fetch device details for each unique device ID
            const deviceDataMap = new Map<string, any>();
            const deviceIconMap = new Map<string, any>();

            await Promise.all(
                Array.from(allDeviceIds).map(async (devId) => {
                    try {
                        const result = await getDeviceById(devId).unwrap();
                        if (result?.data) {
                            deviceDataMap.set(devId, result.data);
                        }
                    } catch (err) {
                        console.error('Failed to fetch device details for enrichment:', devId, err);
                    }
                })
            );

            // Fetch icons from hierarchy for all devices
            await Promise.all(
                Array.from(deviceDataMap.keys()).map(async (devId) => {
                    const deviceData = deviceDataMap.get(devId);
                    const icon = await fetchDeviceIconFromHierarchy(devId, deviceData?.groupId);
                    deviceIconMap.set(devId, icon);
                })
            );

            for (const devAction of deviceActions) {
                const actionIdx = createdActions.indexOf(devAction);
                actionEnrichmentMap.set(
                    actionIdx,
                    buildDeviceActionEnrichmentFromMaps(devAction, deviceDataMap, deviceIconMap, t)
                );
            }

            if (actionEnrichmentMap.size === 0) {
                hasEnrichedDeviceActionsRef.current = enrichKey;
                return;
            }

            const enriched = createdActions.map((actionItem, idx) => {
                const enrichment = actionEnrichmentMap.get(idx);
                if (!enrichment) return actionItem;
                return {
                    ...actionItem,
                    path: enrichment.path,
                    deviceIcon: enrichment.deviceIcon,
                };
            });

            hasEnrichedDeviceActionsRef.current = enrichKey;
            setCreatedActions(enriched);

            // Re-take snapshot after enrichment so isDirty is not falsely true
            requestAnimationFrame(() => {
                snapshotRef.current = JSON.stringify({
                    name: automationName,
                    description: automationDescription,
                    triggerType,
                    selectedLoadId,
                    triggerEvents: triggerEvents.map((e) => ({ onSet: e.onSet, onClear: e.onClear })),
                    actions: enriched.map((a) => ({
                        type: a.type,
                        targetIds: a.targetIds,
                        enabled: a.enabled,
                        actionId: a.actionId,
                        userNote: a.userNote,
                    })),
                });
            });
        };

        void enrichDeviceActions();
    }, [createdActions, isEditMode, balancingId]);

    React.useEffect(() => {
        if (!isEditMode) return;
        if (!automationDetails?.data) {
            snapshotRef.current = null;
            return;
        }
        const id = requestAnimationFrame(() => {
            snapshotRef.current = getFormFingerprint();
        });
        return () => cancelAnimationFrame(id);
    }, [automationDetails, allActions]);

    React.useEffect(() => {
        snapshotRef.current = null;
        hasUserEditedNameRef.current = false;
        hasUserEditedDescriptionRef.current = false;
        hasEnrichedActionsRef.current = null;
        hasEnrichedLoadGroupActionsRef.current = null;
        hasEnrichedDeviceActionsRef.current = null;
        hasHydratedRef.current = false;
    }, [balancingId]);

    const [editingActionId, setEditingActionId] = React.useState<string | null>(null);

    const [isActionModalOpen, setIsActionModalOpen] = React.useState(false);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [actionIdToDelete, setActionIdToDelete] = React.useState<string | null>(null);

    const isCreateActionEnabled = selectedLoadId && hasOnSetChecked;

    const handleLoadGroupEventChange = React.useCallback((events: LoadGroupEvent[]): void => {
        setTriggerEvents((prev) => {
            if (JSON.stringify(prev) === JSON.stringify(events)) {
                return prev;
            }
            return events;
        });

        const onSetChecked = events.some((e) => e.onSet === true || e.onClear === true);
        setHasOnSetChecked(onSetChecked);
    }, []);

    const isEventTrulySelected = (event: { onSet: boolean; onClear: boolean }): boolean =>
        event.onSet === true || event.onClear === true;

    const getNormalizedGroupPath = (node: any): string[] => getNormalizedGroupPathFromNode(node);

    const getSharedGroupPath = (nodes: any[]): string[] | null => {
        if (nodes.length === 0) {
            return [];
        }

        const normalizedPaths = nodes.map(getNormalizedGroupPath);
        const hasAnyGroupPath = normalizedPaths.some((path) => path.length > 0);

        if (!hasAnyGroupPath) {
            return null;
        }

        const [firstPath, ...remainingPaths] = normalizedPaths;
        const allNodesShareSamePath = remainingPaths.every(
            (path) => path.length === firstPath.length && path.every((segment, index) => segment === firstPath[index])
        );

        return allNodesShareSamePath ? [...firstPath] : null;
    };

    const getNodeDeviceId = (node: any): string => getNodeDeviceIdPrimary(node) ?? getNodeDeviceIdParent(node) ?? '';

    const getNodeDeviceName = (node: any): string =>
        getNodeDeviceNamePrimary(node) ?? getNodeDeviceNameParent(node) ?? '';

    const getActionTargetIcon = (node: any, type: CreatedAction['type']): any => getActionTargetIconValue(node, type);

    // Recursively search the hierarchy tree to find a device by ID and extract its icon
    const searchDeviceIconInHierarchy = (node: any, targetDeviceId: string): any =>
        findDeviceIconInTreeNode(node, targetDeviceId);

    const findCachedDeviceIcon = (deviceId: string): DeviceIconLookup => {
        if (deviceIconCacheRef.current.has(deviceId)) {
            return { hit: true, icon: deviceIconCacheRef.current.get(deviceId) };
        }
        return { hit: false, icon: undefined };
    };

    const lookupDeviceIconInChildGroup = async (deviceId: string, groupId: string): Promise<any> => {
        const childResult = await getAllGroups({ parent: groupId }).unwrap();
        const childRecords = childResult?.data?.records || [];
        const matchedDevice = childRecords.find((record: any) => record?.id === deviceId);
        return matchedDevice?.icon ?? null;
    };

    const lookupDeviceIconInRootHierarchy = async (deviceId: string): Promise<any> => {
        const result = await getAllGroups({}).unwrap();
        const hierarchyData = result?.data?.records || result?.data || [];
        for (const rootNode of hierarchyData) {
            const foundIcon = searchDeviceIconInHierarchy(rootNode, deviceId);
            if (foundIcon) return foundIcon;
        }
        return null;
    };

    // Fetch device icon from hierarchy API, with caching
    const fetchDeviceIconFromHierarchy = async (deviceId: string, groupId?: string): Promise<any> => {
        const cached = findCachedDeviceIcon(deviceId);
        if (cached.hit) {
            return cached.icon;
        }

        try {
            if (groupId) {
                const childIcon = await lookupDeviceIconInChildGroup(deviceId, groupId);
                if (childIcon) {
                    deviceIconCacheRef.current.set(deviceId, childIcon);
                    return childIcon;
                }
            }
            const rootIcon = await lookupDeviceIconInRootHierarchy(deviceId);
            if (rootIcon) {
                deviceIconCacheRef.current.set(deviceId, rootIcon);
                return rootIcon;
            }
            deviceIconCacheRef.current.set(deviceId, {});
            return {};
        } catch (err) {
            deviceIconCacheRef.current.set(deviceId, {});
            return {};
        }
    };

    const getSharedDeviceName = (nodes: any[]): string | null => {
        if (nodes.length === 0) {
            return null;
        }

        const deviceIds = nodes.map(getNodeDeviceId).filter(Boolean);
        if (deviceIds.length === nodes.length && new Set(deviceIds).size === 1) {
            return getNodeDeviceName(nodes[0]) || null;
        }

        const deviceNames = nodes.map(getNodeDeviceName).filter(Boolean);
        if (deviceNames.length === nodes.length && deviceNames.every((name) => name === deviceNames[0])) {
            return deviceNames[0];
        }

        return null;
    };

    const getActionSummaryPath = (nodes: any[]): string[] => {
        if (nodes.length === 0) {
            return [];
        }

        const sharedGroupPath = getSharedGroupPath(nodes);
        if (sharedGroupPath) {
            return sharedGroupPath;
        }

        return nodes.length === 1 ? getNormalizedGroupPath(nodes[0]) : [t('WIDGETS:BALANCING.MULTIPLE_GROUPS')];
    };

    const buildBalancingActionPayloadEntry = (actionItem: any, index: number): any => {
        const actionObj: any = {
            actionId: actionItem.actionId || '',
            notes: actionItem.userNote || '',
            order: index,
            triggerIds: actionItem.targetIds,
            type: actionItem.type === 'load' ? 'LOADS' : actionItem.type === 'loadGroup' ? 'LOAD_GROUPS' : 'DEVICE',
        };
        if (actionItem.type !== 'device') {
            actionObj.enabled = actionItem.enabled;
        }
        return actionObj;
    };

    const buildBasePayload = (isLoadGroup: boolean): any => ({
        balancingActions: createdActions.map((actionItem, index) =>
            buildBalancingActionPayloadEntry(actionItem, index)
        ),
        balancingTriggerId: selectedLoadId,
        description: automationDescription,
        loadBalancing: true,
        name: automationName,
        triggerCategory: isLoadGroup ? 'LOAD_GROUPS' : 'LOADS',
    });

    const applyEventsToPayload = (payload: any): void => {
        const selectedEvents = triggerEvents
            .filter((e) => !e.channelId && isEventTrulySelected(e))
            .map((e) => ({ id: e.id, onSet: e.onSet, onClear: e.onClear }));
        payload.events = selectedEvents.length > 0 ? selectedEvents : null;
    };

    const applyLoadGroupDefaults = (payload: any): void => {
        payload.delay = 0;
        payload.isAllEvents = false;
        payload.isEnabled = false;
        payload.isLoadEnabled = false;
        payload.isLoadGroupEnabled = false;
        payload.threshold = null;
        payload.thresholds = null;
    };

    const buildThresholdFromEvent = (event: any): any => {
        const parts = event.id.split('_');
        const suffix = parts.pop() || '';
        const prefix = parts.pop() || '';
        const unitKey = parts.pop() || '';
        return {
            name: `${prefix}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`,
            displayName: [prefix, unitKey, suffix]
                .filter(Boolean)
                .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                .join(' '),
            onClear: event.onClear,
            onSet: event.onSet,
            severity: suffix.charAt(0).toUpperCase() + suffix.slice(1),
            thresholdId: event.channelId,
        };
    };

    const collectGroupDeviceIds = (): GroupDeviceIdsResult => {
        const allDeviceIds = new Set<string>();
        const allGroupIds = new Set<string>();

        if (selectedDeviceId) allDeviceIds.add(selectedDeviceId);
        if (selectedGroupId) allGroupIds.add(selectedGroupId);

        createdActions.forEach((actionItem) => {
            actionItem.targetNodes?.forEach((node: any) => {
                if (node?.deviceId) allDeviceIds.add(node.deviceId);
                if (node?.groupId) allGroupIds.add(node.groupId);
            });
            actionItem.targetIds?.forEach((tid: string) => {
                const parsedDeviceId = getDeviceIdFromTriggerId(tid);
                if (parsedDeviceId) allDeviceIds.add(parsedDeviceId);
            });
        });

        return { devices: Array.from(allDeviceIds), groups: Array.from(allGroupIds) };
    };

    const applyLoadModeFields = (payload: any): void => {
        payload.deviceTypes = {
            allDeviceTypes: false,
            ids: selectedDeviceTypeId ? [selectedDeviceTypeId] : [],
        };
        const validThresholds = triggerEvents
            .filter((event) => event.channelId && isEventTrulySelected(event))
            .map(buildThresholdFromEvent);
        payload.thresholds = validThresholds.length > 0 ? validThresholds : null;
        payload.groupDevices = collectGroupDeviceIds();
    };

    const persistAutomation = async (payload: any): Promise<void> => {
        if (isEditMode) {
            await updateAutomation({ automationId: balancingId, payload }).unwrap();
        } else {
            await createAutomation(payload).unwrap();
        }
    };

    const notifySaveSuccess = (): void => {
        dispatch(
            setMessageContent({
                isOpen: true,
                message: isEditMode ? t('WIDGETS:BALANCING.UPDATE_SUCCESS') : t('WIDGETS:BALANCING.SAVE_SUCCESS'),
                duration: 3000,
            })
        );
    };

    const navigateAfterSave = (): void => {
        if (previousRoute) {
            navigate(previousRoute, { state: { subTab: 'balancing' } });
        } else {
            navigate(-1);
        }
    };

    const handleSaveAutomationError = (error: any): void => {
        const apiErrorMessage = getApiErrorMessage(error);
        if (apiErrorMessage) {
            if (apiErrorMessage.toLowerCase() === 'please enter a valid automation name') {
                setAutomationNameError(t('WIDGETS:BALANCING.AUTOMATION_NAME_REQUIRED'));
            } else {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: apiErrorMessage,
                        duration: 5000,
                    })
                );
            }
        }
        console.error('Failed to save automation:', error);
    };

    const handleSaveAutomation = async (): Promise<void> => {
        setHasAttemptedSave(true);
        const isAutomationNameValid = validateAutomationName(automationName, true);

        if (!isAutomationNameValid) {
            return;
        }

        const isLoadGroup = triggerType === 'loadGroups';
        const payload: any = buildBasePayload(isLoadGroup);
        applyEventsToPayload(payload);

        if (isLoadGroup) {
            applyLoadGroupDefaults(payload);
        } else {
            applyLoadModeFields(payload);
        }

        if (!payload.events || (Array.isArray(payload.events) && payload.events.length === 0)) {
            payload.events = null;
        }

        try {
            await persistAutomation(payload);
            notifySaveSuccess();
            setTriggerEvents([]);
            setHasOnSetChecked(false);
            navigateAfterSave();
        } catch (error: any) {
            handleSaveAutomationError(error);
        }
    };

    const buildLoadActionData = (selectedNodes: any[]): CreatedAction => {
        const uniqueDeviceIds = new Set(selectedNodes.map((n) => n.deviceId));
        const isSingleDevice = uniqueDeviceIds.size === 1;
        const isSingleLoad = selectedNodes.length === 1;
        const firstNode = selectedNodes[0];
        const path = getActionSummaryPath(selectedNodes);
        const selectedDynamicAction = findDynamicActionByKey(dynamicActionsData, action);
        const singleDeviceName = firstNode.deviceName || firstNode.deviceId;

        return {
            id: editingActionId || `${Date.now()}-loads`,
            actionType: resolveActionTypeLabel(selectedDynamicAction, action),
            type: 'load',
            targetName: isSingleLoad ? firstNode.name : t('WIDGETS:BALANCING.MULTIPLE_LOADS'),
            deviceName: isSingleDevice ? singleDeviceName : t('WIDGETS:BALANCING.MULTIPLE_DEVICES'),
            path: path,
            description: '',
            showPath: true,
            userNote: actionNote,
            deviceIcon: isSingleDevice ? getActionTargetIcon(firstNode, 'load') : undefined,
            targetIds: selectedNodes.map((n) => n.id),
            targetNodes: selectedNodes,
            enabled: resolveActionEnabled(selectedDynamicAction),
            actionId: action,
        };
    };

    const buildLoadGroupActionData = (selectedNodes: any[]): CreatedAction => {
        const isSingleLoadGroup = selectedNodes.length === 1;
        const firstNode = selectedNodes[0];
        const sharedDeviceName = getSharedDeviceName(selectedNodes);
        const isSingleDevice = !!sharedDeviceName;
        const path = getActionSummaryPath(selectedNodes);
        const selectedDynamicAction = findDynamicActionByKey(dynamicActionsData, action);
        const targetIds = Array.isArray(modalSelectedLoadId) ? modalSelectedLoadId : [modalSelectedLoadId];

        return {
            id: editingActionId || `${Date.now()}-groups`,
            actionType: resolveActionTypeLabel(selectedDynamicAction, action),
            type: 'loadGroup',
            targetName: isSingleLoadGroup ? firstNode.name : t('WIDGETS:BALANCING.MULTIPLE_LOAD_GROUPS'),
            deviceName: isSingleDevice ? sharedDeviceName : t('WIDGETS:BALANCING.MULTIPLE_DEVICES'),
            path: isSingleDevice ? path : [],
            description: isSingleLoadGroup ? firstNode.description : '',
            showPath: true,
            userNote: actionNote,
            deviceIcon: getActionTargetIcon(firstNode, 'loadGroup'),
            targetIds,
            targetNodes: selectedNodes,
            enabled: resolveActionEnabled(selectedDynamicAction),
            actionId: action,
        };
    };

    const buildDeviceActionData = (selectedNodes: any[]): CreatedAction => {
        const isSingleDevice = selectedNodes.length === 1;
        const firstNode = selectedNodes[0];
        const selectedActionItem = findDynamicActionByKey(allActions, action);
        const path = getActionSummaryPath(selectedNodes);
        const targetIds = Array.isArray(modalSelectedLoadId) ? modalSelectedLoadId : [modalSelectedLoadId];

        return {
            id: editingActionId || `${Date.now()}-devices`,
            actionType: resolveActionTypeLabel(selectedActionItem, action),
            type: 'device',
            targetName: isSingleDevice ? firstNode.name : t('WIDGETS:BALANCING.MULTIPLE_DEVICES'),
            deviceName: '',
            path: path,
            description: isSingleDevice ? firstNode.location || '' : '',
            showPath: true,
            userNote: actionNote,
            deviceIcon: getActionTargetIcon(firstNode, 'device'),
            targetIds,
            targetNodes: selectedNodes,
            actionId: action,
        };
    };

    const buildActionDataForCurrentSelection = (selectedNodes: any[]): CreatedAction => {
        if (modalTriggerType === 'loads') return buildLoadActionData(selectedNodes);
        if (modalTriggerType === 'loadGroups') return buildLoadGroupActionData(selectedNodes);
        return buildDeviceActionData(selectedNodes);
    };

    const commitActionToList = (actionData: CreatedAction): void => {
        if (editingActionId) {
            setCreatedActions((prev) => prev.map((item) => (item.id === editingActionId ? actionData : item)));
        } else {
            setCreatedActions((prev) => [...prev, actionData]);
        }
    };

    const resetActionModalState = (): void => {
        setEditingActionId(null);
        setAction('');
        setModalTriggerType('');
        setModalSelectedLoadId([]);
        setModalSelectedNodes([]);
        setActionNote('');
    };

    const isSaveActionDisabled = (): boolean =>
        !modalSelectedLoadId || (Array.isArray(modalSelectedLoadId) && modalSelectedLoadId.length === 0) || !action;

    const handleSaveAction = (): void => {
        if (isSaveActionDisabled()) {
            return;
        }
        const actionData = buildActionDataForCurrentSelection(modalSelectedNodes);
        commitActionToList(actionData);
        resetActionModalState();
        dispatch(
            setMessageContent({
                isOpen: true,
                message: editingActionId
                    ? t('WIDGETS:BALANCING.ACTION_UPDATE_SUCCESS')
                    : t('WIDGETS:BALANCING.ACTION_CREATE_SUCCESS'),
                duration: 3000,
            })
        );
        setIsActionModalOpen(false);
    };
    const handleDeleteAction = (actionId: string): void => {
        setActionIdToDelete(actionId);
        setIsDeleteModalOpen(true);
    };
    const handleConfirmDeleteAction = (): void => {
        if (actionIdToDelete) {
            setCreatedActions((prev) => prev.filter((item) => item.id !== actionIdToDelete));
            setActionIdToDelete(null);
            setIsDeleteModalOpen(false);
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:BALANCING.ACTION_DELETE_SUCCESS'),
                    duration: 3000,
                })
            );
        }
    };

    const handleCancelDeleteAction = (): void => {
        setActionIdToDelete(null);
        setIsDeleteModalOpen(false);
    };

    const handleDeleteLoadGroupAction = (actionId: string): void => {
        setTempLoadGroupActions((prev) => prev.filter((item) => item.id !== actionId));
    };

    const handleEditAction = (actionId: string): void => {
        const actionToEdit = createdActions.find((item) => item.id === actionId);
        if (!actionToEdit) return;

        setEditingActionId(actionId);
        // For loads/loadGroups, the API returns action: null.
        // The action is determined by type + enabled (true=on, false=off).
        // We need to resolve the correct radio key from dynamicActionsData.
        setAction(resolveEditActionId(actionToEdit, dynamicActionsData));
        setModalTriggerType(resolveEditActionModalType(actionToEdit.type));
        setModalSelectedLoadId(actionToEdit.targetIds);
        setModalSelectedNodes(actionToEdit.targetNodes || []);
        setActionNote(actionToEdit.userNote || '');
        setIsActionModalOpen(true);
    };

    // When editing a load/loadGroup action, the correct action key may not resolve
    // immediately (e.g. for loadGroups, dynamicActionsData refetches with a new body).
    // This effect re-resolves once the data is available.
    React.useEffect(() => {
        if (
            isResolveEditActionEffectSkipped(
                editingActionId,
                isActionModalOpen,
                modalTriggerType,
                dynamicActionsData,
                isDynamicActionsFetching
            )
        ) {
            return;
        }

        const actionToEdit = createdActions.find((item) => item.id === editingActionId);
        if (!actionToEdit) return;

        const resolvedKey = findResolvedActionKey(dynamicActionsData, actionToEdit);
        if (resolvedKey) setAction(resolvedKey);
    }, [
        dynamicActionsData,
        isDynamicActionsFetching,
        editingActionId,
        isActionModalOpen,
        modalTriggerType,
        createdActions,
    ]);

    const handleModalClose = (): void => {
        setIsActionModalOpen(false);
        setEditingActionId(null);
        setAction('');
        setModalTriggerType('');
        setModalSelectedLoadId([]);
        setModalSelectedNodes([]);
        setActionNote('');
    };

    const onDragEnd = (result: DropResult): void => {
        const reordered = handleDragEndReorder(result, createdActions);
        if (reordered) {
            setCreatedActions(reordered);
        }
    };

    const buildActionModalActions = (): ModalActions[] => [
        {
            key: 'cancel',
            label: <CustomTransComponent translationKey="COMMON:CANCEL" />,
            close: true,
            variant: 'outlined',
            handleClick: handleModalClose,
        },
        {
            key: 'save',
            label: <SaveActionLabel isEditing={!!editingActionId} />,
            variant: 'contained',
            handleClick: handleSaveAction,
            disabled: isSaveActionDisabled(),
        },
    ];

    const actionModalActions: ModalActions[] = buildActionModalActions();

    const hasValidModalTargetType = isModalTargetTypeValid(modalTriggerType);

    return (
        <>
            <AppBar position="sticky" elevation={1} sx={{ backgroundColor: '#fff', color: 'text.primary' }}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <AppBarActions
                        isSaving={isSaving}
                        isCreatingAutomation={isCreatingAutomation}
                        isUpdatingAutomation={isUpdatingAutomation}
                        isEditMode={isEditMode}
                        automationDetails={automationDetails}
                        snapshotRef={snapshotRef}
                        isDirty={isDirty}
                        automationName={automationName}
                        createdActions={createdActions}
                        previousRoute={previousRoute}
                        navigate={navigate}
                        handleSaveAutomation={handleSaveAutomation}
                    />
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Paper
                    elevation={1}
                    sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            <CustomTransComponent translationKey={'WIDGETS:BALANCING.AUTOMATION_DETAILS'} />
                        </Typography>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33%' } }}>
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    label={<CustomTransComponent translationKey={'COMMON:AUTOMATION_NAME'} customT />}
                                    required={isNameRequired}
                                    value={automationName}
                                    onChange={handleAutomationNameChange}
                                    onBlur={() => validateAutomationName(automationName)}
                                    error={isTextFieldError(
                                        automationName.length,
                                        NAME_CHAR_LIMIT,
                                        automationNameError
                                    )}
                                    helperText={
                                        <CharCounterHelperText
                                            errorMessage={automationNameError}
                                            length={automationName.length}
                                            limit={NAME_CHAR_LIMIT}
                                        />
                                    }
                                    inputProps={{ maxLength: NAME_CHAR_LIMIT }}
                                />
                            </Box>

                            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 67%' } }}>
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} customT />}
                                    value={automationDescription}
                                    onChange={(e) => {
                                        const value = e.target.value.slice(0, DESC_CHAR_LIMIT);
                                        hasUserEditedDescriptionRef.current = true;
                                        setAutomationDescription(value);
                                        validateAutomationDescription(value);
                                    }}
                                    error={isTextFieldError(
                                        automationDescription.length,
                                        DESC_CHAR_LIMIT,
                                        automationDescriptionError
                                    )}
                                    helperText={
                                        <CharCounterHelperText
                                            errorMessage={automationDescriptionError}
                                            length={automationDescription.length}
                                            limit={DESC_CHAR_LIMIT}
                                        />
                                    }
                                    inputProps={{ maxLength: DESC_CHAR_LIMIT }}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                </Paper>

                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Stack flexDirection={'row'} justifyContent={'space-between'}>
                        <Box width={'32.5%'}>
                            <Paper
                                elevation={0}
                                sx={{
                                    py: 3,
                                    px: 2,
                                    height: '100%',
                                    borderRadius: 4,
                                    backgroundColor: theme.palette.background.default,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        pt: 3,
                                        height: '100%',
                                        backgroundColor: theme.palette.background.default,
                                    }}
                                >
                                    <Box pb={2}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            1.
                                            <CustomTransComponent translationKey="WIDGETS:BALANCING.SELECT_TRIGGER_HEADING" />
                                        </Typography>
                                    </Box>
                                    <Box p={2} pb={0} sx={{ backgroundColor: '#fff' }}>
                                        <FormControl variant="filled" fullWidth>
                                            <InputLabel id="trigger-type-label">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CustomTransComponent translationKey="WIDGETS:BALANCING.TRIGGER_TYPE_LABEL" />
                                                </Box>
                                            </InputLabel>
                                            <Select
                                                labelId="trigger-type-label"
                                                id="trigger-type"
                                                value={triggerType}
                                                onChange={handleTriggerTypeChange}
                                            >
                                                <MenuItem value="loadGroups">
                                                    {t('WIDGETS:BALANCING.TARGET_LOAD_GROUPS', {
                                                        defaultValue: 'Load Groups',
                                                    })}
                                                </MenuItem>
                                                <MenuItem value="loads">
                                                    {t('WIDGETS:BALANCING.TARGET_LOADS', { defaultValue: 'Loads' })}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box
                                        sx={{
                                            overflow: 'auto',
                                            height: 'calc(100vh - 364px)',
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <TriggerColumnBody
                                            theme={theme}
                                            t={t}
                                            triggerType={triggerType}
                                            selectedLoadId={selectedLoadId}
                                            handleTriggerLoadSelect={handleTriggerLoadSelect}
                                            setSelectedLoadId={setSelectedLoadId}
                                            setTempLoadGroupActions={setTempLoadGroupActions}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>

                        <Box width={'32.5%'}>
                            <Paper
                                elevation={0}
                                sx={{
                                    py: 3,
                                    px: 2,
                                    height: '100%',
                                    borderRadius: 4,
                                    backgroundColor: theme.palette.background.default,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        pt: 3,
                                        height: '100%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box pb={2}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            2.
                                            <CustomTransComponent translationKey="WIDGETS:BALANCING.SELECT_TRIGGER_EVENT_HEADING" />
                                        </Typography>
                                    </Box>
                                    <Box sx={{ height: '100%', backgroundColor: '#fff' }}>
                                        <EventColumnBody
                                            theme={theme}
                                            t={t}
                                            selectedLoadId={selectedLoadId}
                                            triggerType={triggerType}
                                            handleLoadGroupEventChange={handleLoadGroupEventChange}
                                            setHasOnSetChecked={setHasOnSetChecked}
                                            initialHydratedEventsRef={initialHydratedEventsRef}
                                            selectedDeviceId={selectedDeviceId}
                                            selectedDeviceTypeId={selectedDeviceTypeId}
                                            deviceTypeIds={deviceTypeIds}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>

                        <Box width={'32.5%'}>
                            <Paper
                                elevation={0}
                                sx={{
                                    py: 3,
                                    px: 2,
                                    height: '100%',
                                    borderRadius: 4,
                                    backgroundColor: theme.palette.background.default,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        pt: 3,
                                        height: '100%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box pb={2}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            3.
                                            <CustomTransComponent translationKey="WIDGETS:BALANCING.CREATE_ACTIONS_HEADING" />
                                        </Typography>
                                    </Box>
                                    <Box sx={{ height: '100%', backgroundColor: '#fff' }}>
                                        <Box p={2}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                size="medium"
                                                disabled={!isCreateActionEnabled}
                                                sx={{
                                                    justifyContent: 'center',
                                                    textTransform: 'none',
                                                    borderStyle: 'solid',
                                                }}
                                                onClick={() => {
                                                    setEditingActionId(null);
                                                    setAction('');
                                                    setModalTriggerType('');
                                                    setModalSelectedLoadId([]);
                                                    setModalSelectedNodes([]);
                                                    setActionNote('');
                                                    setIsActionModalOpen(true);
                                                }}
                                            >
                                                <Add sx={{ mr: 1 }} />
                                                {/* +{' '} */}
                                                {t('WIDGETS:BALANCING.CREATE_ACTION', {
                                                    defaultValue: 'Create Action',
                                                })}
                                            </Button>
                                        </Box>
                                        <Box
                                            sx={{
                                                mt: 2,
                                                overflow: 'auto',
                                                height: 'calc(100vh - 360px)',
                                            }}
                                        >
                                            <ActionsListSection
                                                tempLoadGroupActions={tempLoadGroupActions}
                                                createdActions={createdActions}
                                                theme={theme}
                                                t={t}
                                                onDragEnd={onDragEnd}
                                                onDeleteLoadGroupAction={handleDeleteLoadGroupAction}
                                                onEditAction={handleEditAction}
                                                onDeleteAction={handleDeleteAction}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    </Stack>
                </Paper>

                <CustomModal
                    isOpen={isActionModalOpen}
                    header={<CustomTransComponent translationKey="WIDGETS:BALANCING.SELECT_TARGET_ACTION_DESC" />}
                    actions={actionModalActions}
                    actionsDivider
                    type="large"
                    contentClass="overflow-hidden"
                >
                    <Stack spacing={0} sx={{ p: 0 }}>
                        <Box
                            sx={{
                                p: 1,
                                backgroundColor: theme.palette.background.default,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: '0 0 525px', minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        1.
                                        <CustomTransComponent translationKey="WIDGETS:BALANCING.SELECT_TARGET_HEADING" />
                                    </Typography>
                                    <FormControl variant="filled" fullWidth sx={{ mb: 2 }}>
                                        <InputLabel id="modal-target-type">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {!modalTriggerType && <BoltIcon sx={{ fontSize: 20 }} />}
                                                <CustomTransComponent translationKey="WIDGETS:BALANCING.TARGET_TYPE" />
                                            </Box>
                                        </InputLabel>
                                        <Select
                                            labelId="modal-target-type"
                                            value={modalTriggerType}
                                            onChange={handleModalTriggerTypeChange}
                                        >
                                            <MenuItem value="devices">
                                                <CustomTransComponent translationKey="WIDGETS:BALANCING.TARGET_DEVICES" />
                                            </MenuItem>

                                            <MenuItem value="loadGroups">
                                                <CustomTransComponent translationKey="WIDGETS:BALANCING.TARGET_LOAD_GROUPS" />
                                            </MenuItem>
                                            <MenuItem value="loads">
                                                <CustomTransComponent translationKey="WIDGETS:BALANCING.TARGET_LOADS" />
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Box sx={{ height: '33vh', display: 'flex', flexDirection: 'column' }}>
                                        <ModalTargetSelector
                                            modalTriggerType={modalTriggerType}
                                            modalSelectedLoadId={modalSelectedLoadId}
                                            handleModalLoadSelect={handleModalLoadSelect}
                                            setModalSelectedLoadId={setModalSelectedLoadId}
                                            setModalSelectedNodes={setModalSelectedNodes}
                                        />
                                    </Box>
                                </Box>

                                {hasValidModalTargetType && (
                                    <>
                                        <Divider orientation="vertical" flexItem />

                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                2.
                                                <CustomTransComponent translationKey="WIDGETS:BALANCING.APPLY_ACTION_HEADING" />
                                            </Typography>
                                            <RadioGroup
                                                name="action"
                                                value={action}
                                                onChange={(e) => setAction(e.target.value)}
                                            >
                                                <Stack spacing={1}>
                                                    <ModalActionRadios
                                                        modalTriggerType={modalTriggerType}
                                                        allActions={allActions}
                                                        isDynamicActionsLoading={isDynamicActionsLoading}
                                                        isDynamicActionsFetching={isDynamicActionsFetching}
                                                        dynamicActionsData={dynamicActionsData}
                                                    />
                                                </Stack>
                                            </RadioGroup>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>

                        <Box sx={{ py: 1 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={1}
                                variant="outlined"
                                placeholder={t('WIDGETS:BALANCING.NOTES_PLACEHOLDER')}
                                value={actionNote}
                                onChange={(e) => setActionNote(e.target.value)}
                            />
                        </Box>
                    </Stack>
                </CustomModal>
                <ConfirmModal
                    isVisible={isDeleteModalOpen}
                    header={t('WIDGETS:BALANCING.DELETE_ACTION_BUTTON', { defaultValue: 'Delete Action?' })}
                    description={
                        <CustomTransComponent translationKey="WIDGETS:BALANCING.DELETE_ACTION_CONFIRM_MESSAGE" />
                    }
                    isLoading={false}
                    onClose={handleCancelDeleteAction}
                    confirmClick={handleConfirmDeleteAction}
                    onCancel={() => setIsDeleteModalOpen(false)}
                    actionButtonText={t('WIDGETS:BALANCING.DELETE_ACTION_BUTTON')}
                    buttonColor="error"
                />
            </Container>
        </>
    );
};
